import { ReactNode } from 'react'

import styled from 'styled-components'
import { twMerge } from 'tailwind-merge'

const RenderedRichTextContainer = styled.div`
  padding: 0;
  h1 {
    font-size: 2em;
  }
  h2 {
    font-size: 1.5em;
  }
  h3 {
    font-size: 1.17em;
  }
  h4 {
    font-size: 1em;
  }
  h5 {
    font-size: 0.83em;
  }
  h6 {
    font-size: 0.67em;
  }
  ul {
    display: block;
    padding-left: 1.5em;
    margin: 0.5em 0;
  }
  ol {
    display: block;
    padding-left: 1.5em;
    margin: 0.5em 0;
  }
  li {
    margin-bottom: 0.25em;
  }
  /*
   * Quill v1 HTML uses standard <ul>/<ol> with plain <li> elements,
   * including nested lists for sub-items. Quill's ql-editor CSS sets
   * list-style-type:none on all li, so we override on li:not([data-list])
   * to restore native markers. Nested ordered lists cycle through
   * decimal → lower-alpha → lower-roman to match the Quill editor.
   */
  ul > li:not([data-list]) {
    display: list-item;
    list-style-type: disc;
  }
  ol > li:not([data-list]) {
    display: list-item;
    list-style-type: decimal;
  }
  ol ol > li:not([data-list]) {
    list-style-type: lower-alpha;
  }
  ol ol ol > li:not([data-list]) {
    list-style-type: lower-roman;
  }
  ol ol ol ol > li:not([data-list]) {
    list-style-type: decimal;
  }
  /*
   * Quill v2 uses data-list attributes on li elements within ol tags.
   * The live editor renders markers via .ql-ui::before pseudo-elements,
   * but saved HTML has no .ql-ui spans. We replicate the marker rendering
   * here using ::before on the li itself, matching Quill's counter system
   * (list-0 through list-9) which is already managed by ql-editor CSS.
   */
  li[data-list] {
    list-style-type: none;
  }
  li[data-list]::before {
    display: inline-block;
    margin-left: -1.5em;
    margin-right: 0.3em;
    text-align: right;
    white-space: nowrap;
    width: 1.2em;
  }
  li[data-list='bullet']::before {
    content: '\\2022';
  }
  li[data-list='ordered']::before {
    content: counter(list-0, decimal) '. ';
  }
  li[data-list='ordered'].ql-indent-1::before {
    content: counter(list-1, lower-alpha) '. ';
  }
  li[data-list='ordered'].ql-indent-2::before {
    content: counter(list-2, lower-roman) '. ';
  }
  li[data-list='ordered'].ql-indent-3::before {
    content: counter(list-3, decimal) '. ';
  }
  li[data-list='ordered'].ql-indent-4::before {
    content: counter(list-4, lower-alpha) '. ';
  }
  li[data-list='ordered'].ql-indent-5::before {
    content: counter(list-5, lower-roman) '. ';
  }
  p {
    margin: 0;
  }
`

type Props = {
  children: ReactNode
  className?: string
}
const RenderedRichTextInput = ({ children, className }: Props) => {
  if (!children) {
    return
  }

  // Quill saves all spaces as &nbsp; which prevents normal word wrapping
  const html = String(children).replaceAll('&nbsp;', ' ')

  return (
    <RenderedRichTextContainer
      dangerouslySetInnerHTML={{ __html: html }}
      className={twMerge(
        `ql-editor [&_a]:!text-link [&_a]:hover:!underline [&_a]:focus-visible:!underline`,
        className
      )}
    ></RenderedRichTextContainer>
  )
}

export default RenderedRichTextInput
